import { API_HEADERS, apiFetch, merchantOperationPath } from "@price-rescue/api-contracts";
import type {
  MerchantOperationDescriptor,
  MerchantOperationPathParams,
  MerchantOperationQuery,
} from "@price-rescue/api-contracts";

import { isAuthenticationExpired, toMerchantApiError } from "./features/authentication/errors.js";
import type {
  EmbeddedAppBridgeAdapter,
  MerchantApiClient,
  MerchantApiRequestOptions,
} from "./types.js";

export interface MerchantApiClientOptions {
  baseUrl: string;
  appBridge: EmbeddedAppBridgeAdapter;
  correlationIdFactory?: () => string;
}

type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface MerchantOperationOptions {
  body?: unknown;
  pathParams?: MerchantOperationPathParams;
  query?: MerchantOperationQuery;
  request?: MerchantApiRequestOptions;
}

export function createMerchantApiClient(options: MerchantApiClientOptions): MerchantApiClient {
  return {
    async operation<T>(
      operation: MerchantOperationDescriptor,
      operationOptions?: MerchantOperationOptions,
    ) {
      const path = merchantOperationPath(
        operation,
        operationOptions?.pathParams,
        operationOptions?.query,
      );
      return request<T>(
        options,
        operation.method,
        path,
        operationOptions?.body,
        operationOptions?.request,
      );
    },
    async get<T>(path: string, requestOptions?: MerchantApiRequestOptions) {
      return request<T>(options, "GET", path, undefined, requestOptions);
    },
    async post<T>(path: string, body?: unknown, requestOptions?: MerchantApiRequestOptions) {
      return request<T>(options, "POST", path, body, requestOptions);
    },
    async patch<T>(path: string, body?: unknown, requestOptions?: MerchantApiRequestOptions) {
      return request<T>(options, "PATCH", path, body, requestOptions);
    },
    async put<T>(path: string, body?: unknown, requestOptions?: MerchantApiRequestOptions) {
      return request<T>(options, "PUT", path, body, requestOptions);
    },
    async delete<T>(path: string, body?: unknown, requestOptions?: MerchantApiRequestOptions) {
      return request<T>(options, "DELETE", path, body, requestOptions);
    },
  };
}

async function request<T>(
  options: MerchantApiClientOptions,
  method: HttpMethod,
  path: string,
  body: unknown,
  requestOptions: MerchantApiRequestOptions | undefined,
): Promise<T> {
  try {
    return await sendRequest<T>(options, method, path, body, requestOptions);
  } catch (error: unknown) {
    if (!shouldRetryAfterAuthExpired(method, requestOptions, error)) throw error;
    return sendRequest<T>(options, method, path, body, requestOptions);
  }
}

async function sendRequest<T>(
  options: MerchantApiClientOptions,
  method: HttpMethod,
  path: string,
  body: unknown,
  requestOptions: MerchantApiRequestOptions | undefined,
): Promise<T> {
  const token = await options.appBridge.getSessionToken({
    fresh: requestOptions?.requiresFreshToken === true,
  });
  const result = await apiFetch<T>(
    {
      baseUrl: options.baseUrl,
      authToken: token,
      correlationId: options.correlationIdFactory?.() ?? crypto.randomUUID(),
    },
    path,
    {
      method,
      credentials: "include",
      headers: requestHeaders(requestOptions),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    },
  );

  if (!result.ok) throw toMerchantApiError(result);
  return result.data;
}

function requestHeaders(options: MerchantApiRequestOptions | undefined): Record<string, string> {
  return {
    ...(options?.idempotencyKey === undefined
      ? {}
      : { [API_HEADERS.IDEMPOTENCY_KEY]: options.idempotencyKey }),
  };
}

function shouldRetryAfterAuthExpired(
  method: HttpMethod,
  options: MerchantApiRequestOptions | undefined,
  error: unknown,
): boolean {
  if (!isAuthenticationExpired(error)) return false;
  if (method === "GET") return true;

  return options?.requiresFreshToken === true || options?.idempotencyKey !== undefined;
}
